import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MainContainer from "../UI/MainContainer";
import Select from "react-select";
import axios from "../../store/api/axios";
import { AudioStatus, Instrument, Post } from "../../types/Types";
import Button from "../UI/Button";
import { ButtonTypes, DEFAULT_AUTH_DATA, instrumentChipSelectStyles } from "../../utils/Constants";
import FileDropzone from "../UI/FileDropzone";
import AudioPlayer from "../UI/AudioPlayer";
import CircularLoading from "../UI/CircularLoading";
import usePageTitle from "../../utils/usePageTitle";
import { formatDMYHM } from "../../utils/DateUtils";
import { isAudioFile, isPdfFile } from "../../utils/fileTypes";
import { BsArrowLeft } from "react-icons/bs";
import { CiEdit, CiFileOn, CiFolderOn, CiMusicNote1 } from "react-icons/ci";

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fileNameFromUrl = (url: string) => {
  const last = String(url).split("?")[0].split("/").pop() || String(url);
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

const STATUS_LABEL: Record<AudioStatus, { text: string; className: string }> = {
  PD: { text: "Pending", className: "bg-orange-primary33 text-[#8a4f00]" },
  AP: { text: "Approved", className: "bg-green-primary33 text-[#03644a]" },
  RJ: { text: "Rejected", className: "bg-red-primary/10 text-red-primary" },
};

const inputClass = (invalid = false) =>
  `mt-1 block w-full border px-4 py-2.5 outline-none bg-white placeholder:text-[#9a9a9a] ${
    invalid
      ? "border-red-primary ring-1 ring-inset ring-red-primary"
      : "border-[#D9D9D9] focus:border-primary focus:ring-1 focus:ring-inset focus:ring-primary"
  }`;

const FieldTag: React.FC<{ required?: boolean }> = ({ required }) => (
  <span className={`text-[11px] font-bold tracking-[0.06em] uppercase ${required ? "text-[#526554]" : "text-gray-70"}`}>
    {required ? "Required" : "Optional"}
  </span>
);

const CardTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <h2 className="flex items-center gap-2.5 text-lg font-bold">
    <span className="w-8 h-8 rounded-[10px] bg-primary/[0.07] text-primary grid place-items-center text-xl">{icon}</span>
    {children}
  </h2>
);

const FileRow: React.FC<{
  icon: React.ReactNode;
  name: string;
  meta: string;
  onReplace: () => void;
  onUndo?: () => void;
}> = ({ icon, name, meta, onReplace, onUndo }) => (
  <div className="flex items-center gap-3 bg-gray-f6 rounded-2xl px-3 py-2.5 min-w-0">
    <span className="w-[38px] h-[38px] rounded-xl bg-white border border-gray-e8 text-primary grid place-items-center text-xl shrink-0">
      {icon}
    </span>
    <div className="flex flex-col min-w-0 flex-1">
      <span className="font-semibold text-[15px] truncate">{name}</span>
      <span className="text-[13px] text-gray-70">{meta}</span>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      {onUndo && (
        <button type="button" onClick={onUndo} className="text-[13px] font-semibold text-primary underline underline-offset-[3px] px-1">
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onReplace}
        className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
      >
        Replace
      </button>
    </div>
  </div>
);

const AddEditPost: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!).id : DEFAULT_AUTH_DATA.id
  const { postId } = useParams<{ postId: string }>();
  usePageTitle(postId ? "Edit project" : "New project");
  const [post, setPost] = useState<Post>({
    id: "", user: "1", title: "", description: "",
    instruments: [], mp3_file: "", main_audio_id: "", pdf_file: "", sections: [], created_at: null, is_archived: false
  });
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  // Editing: don't show (or let anyone type into) an empty form that the
  // loaded project would then overwrite.
  const [isLoadingPost, setIsLoadingPost] = useState(!!postId);
  const [loadError, setLoadError] = useState(false);

  const [instrumentOptions, setInstrumentOptions] = useState<{ id: string; name: string }[]>([]);

  const [errors, setErrors] = useState<{ title?: string; description?: string; mp3_file?: string; general?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get('/instruments/');
      setInstrumentOptions(data.map((inst: Instrument) => ({ id: inst.id, name: inst.name })));
    })().catch(console.error);
  }, []);

  useEffect(() => {
    if (!postId) return;
    setIsLoadingPost(true);
    setLoadError(false);
    (async () => {
      const { data } = await axios.get(`/posts/${postId}/`);
      const mp3 = typeof data.mp3_file === "string" && data.mp3_file.startsWith(`${process.env.BASE_URL}`)
        ? data.mp3_file.replace(`${process.env.BASE_URL}`, "/")
        : data.mp3_file;

      const pdf = typeof data.pdf_file === "string" && data.pdf_file.startsWith(`${process.env.BASE_URL}`)
        ? data.pdf_file.replace(`${process.env.BASE_URL}`, "/")
        : data.pdf_file;

      setPost({
        ...data,
        mp3_file: mp3,
        pdf_file: pdf,
        instruments: data.instruments.map((i: Instrument) => ({ id: String(i.id), name: i.name })),
        user: String(data.user ?? "1"),
      });
      setAudioUrl(mp3);
      setPdfUrl(pdf)
    })()
      .catch((err) => {
        console.error(err);
        setLoadError(true);
      })
      .finally(() => setIsLoadingPost(false));
  }, [postId]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const selectAudioFile = (file: File) => {
    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
    if (errors.mp3_file) setErrors(prev => ({ ...prev, mp3_file: undefined }));
  };

  // Back to the project's current track (or to no track on a new project);
  // nothing is uploaded for the main track unless a new file is picked.
  const undoAudioFile = () => {
    setAudioFile(null);
    setAudioUrl(post.mp3_file || null);
  };

  const selectPdfFile = (file: File) => {
    setPdfFile(file);
    setPdfUrl(URL.createObjectURL(file));
  };

  const undoPdfFile = () => {
    setPdfFile(null);
    setPdfUrl(post.pdf_file || null);
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!post.title.trim()) e.title = "Title is required.";
    if (!post.description.trim()) e.description = "Description is required.";
    const hasAudio = !!audioFile || !!post.mp3_file; // allow existing audio on edit
    if (!hasAudio) e.mp3_file = "An audio file is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitPost = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("user", userId);
      formData.append("title", post.title.trim());
      formData.append("description", post.description.trim());
      if (audioFile) formData.append("mp3_file", audioFile);
      if (pdfFile) formData.append("pdf_file", pdfFile);
      post.instruments.forEach(i => formData.append("instruments", i.id));

      if (postId) {
        await axios.put(`/posts/${postId}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        navigate(`/post-details/${postId}`);
      } else {
        const { data } = await axios.post("/posts/add/", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        navigate(`/post-details/${data.id}`);
      }
    } catch (err: any) {
      if (err?.response?.status === 400 && err.response.data) {
        const d = err.response.data;
        setErrors({
          title: Array.isArray(d.title) ? d.title[0] : undefined,
          description: Array.isArray(d.description) ? d.description[0] : undefined,
          mp3_file: Array.isArray(d.mp3_file) ? d.mp3_file[0] : undefined,
          general:
            typeof d === "string"
              ? d
              : d.detail || "Could not save the post. Please fix the errors and try again."
        });
      } else {
        setErrors({ general: "Something went wrong while saving. Please try again." });
      }
      console.error("Error saving post:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const hasAudio = !!audioFile || !!post.mp3_file;
  const hasPdf = !!pdfFile || !!post.pdf_file;
  const cardClass = "bg-white rounded-3xl border border-gray-e8 shadow-md p-5 768:p-6 flex flex-col gap-5 min-w-0";

  if (isLoadingPost || loadError) {
    return (
      <MainContainer>
        <div className="w-full min-h-[400px] py-16 flex flex-col items-center justify-center gap-4 text-center">
          {isLoadingPost ? (
            <>
              <CircularLoading />
              <span className="sr-only" role="status">Loading project…</span>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold">Couldn't load this project.</p>
              <p className="text-black41">Check your connection and try again.</p>
              <Link to="/projects" className="text-primary font-semibold underline underline-offset-[3px]">Back to projects</Link>
            </>
          )}
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="w-full py-10 1200:px-0 px-4">
        <form onSubmit={(e) => { e.preventDefault(); submitPost(); }} className="flex flex-col gap-6">
          <Link
            to={postId ? `/post-details/${postId}` : "/projects"}
            className="flex items-center gap-1.5 w-fit text-sm font-semibold text-primary hover:underline"
          >
            <BsArrowLeft />
            {postId ? "Back to project" : "Back to projects"}
          </Link>

          <div>
            <h1 className="text-3xl font-bold">{postId ? "Edit project" : "New project"}</h1>
            <p className="mt-1 text-gray-600">
              {postId
                ? "Update the details, replace the track or sheet music, and see who has joined."
                : "Share a track and tell musicians what you need."}
            </p>
          </div>

          {errors.general && (
            <div role="alert" className="rounded-2xl bg-red-primary/[0.08] px-4 py-3 text-sm text-[#8a1515]">
              {errors.general}
            </div>
          )}

          <div className="grid 992:grid-cols-[1.15fr_1fr] gap-6 items-start">
            <section className={cardClass}>
              <CardTitle icon={<CiEdit />}>Details</CardTitle>

              <div>
                <div className="flex justify-between items-baseline gap-2">
                  <label htmlFor="title" className="text-sm font-semibold text-gray-700">Title</label>
                  <FieldTag required />
                </div>
                <input
                  type="text"
                  id="title"
                  value={post.title}
                  placeholder="e.g. Night Bus Groove"
                  aria-invalid={!!errors.title}
                  onChange={(e) => { setPost(p => ({ ...p, title: e.target.value })); if (errors.title) setErrors(prev => ({ ...prev, title: undefined })); }}
                  className={`${inputClass(!!errors.title)} rounded-full`}
                />
                {errors.title && <p className="mt-1 text-sm font-semibold text-red-primary">{errors.title}</p>}
              </div>

              <div>
                <div className="flex justify-between items-baseline gap-2">
                  <label htmlFor="description" className="text-sm font-semibold text-gray-700">Description</label>
                  <FieldTag required />
                </div>
                <textarea
                  id="description"
                  value={post.description}
                  placeholder="What should musicians add? Mention tempo, key and style."
                  aria-invalid={!!errors.description}
                  aria-describedby="description-hint"
                  onChange={(e) => { setPost(p => ({ ...p, description: e.target.value })); if (errors.description) setErrors(prev => ({ ...prev, description: undefined })); }}
                  className={`${inputClass(!!errors.description)} rounded-[20px] min-h-[200px] resize-y`}
                />
                {errors.description
                  ? <p className="mt-1 text-sm font-semibold text-red-primary">{errors.description}</p>
                  : <p id="description-hint" className="mt-1 text-[13px] text-gray-70">Tell musicians what you need, plus the tempo, key and style.</p>}
              </div>

              <div>
                <div className="flex justify-between items-baseline gap-2">
                  <label htmlFor="instruments" className="text-sm font-semibold text-gray-700">Instruments you need</label>
                  <FieldTag />
                </div>
                <Select
                  isMulti
                  inputId="instruments"
                  name="instruments"
                  options={instrumentOptions.map((option) => ({ value: option.id, label: option.name }))}
                  value={post.instruments.map((i) => ({ value: i.id, label: i.name }))}
                  className="mt-1"
                  maxMenuHeight={160}
                  classNamePrefix="select"
                  styles={instrumentChipSelectStyles}
                  onChange={(selectedOptions) => {
                    const selectedValues = (selectedOptions ?? []).map((option: any) => ({ id: String(option.value), name: option.label }));
                    setPost(post => ({ ...post, instruments: selectedValues }));
                  }}
                />
                <p className="mt-1 text-[13px] text-gray-70">Each instrument gets its own collaboration section where musicians add their audio.</p>
              </div>
            </section>

            <section className={cardClass}>
              <CardTitle icon={<CiFolderOn />}>Files</CardTitle>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-baseline gap-2">
                  <span className="text-sm font-semibold text-gray-700">Main track</span>
                  <FieldTag required />
                </div>
                <FileDropzone
                  accept="audio/*"
                  inputLabel="Main track"
                  isAcceptedFile={isAudioFile}
                  rejectMessage={(file) => `"${file.name}" isn't an audio file. Choose an audio file such as MP3 or WAV.`}
                  onFile={selectAudioFile}
                  hasFile={hasAudio}
                  invalid={!!errors.mp3_file}
                  emptyTitle="Drag your track here, or"
                  emptyHint="Any audio file, such as MP3 or WAV."
                >
                  {(openPicker) => (
                    <div className="flex flex-col gap-2">
                      <FileRow
                        icon={<CiMusicNote1 />}
                        name={audioFile ? audioFile.name : fileNameFromUrl(post.mp3_file)}
                        meta={audioFile ? `New file · ${formatSize(audioFile.size)}` : "Current file"}
                        onReplace={openPicker}
                        onUndo={audioFile ? undoAudioFile : undefined}
                      />
                      <AudioPlayer
                        key={audioUrl ?? post.mp3_file}
                        className="w-full"
                        size="lg"
                        waveformSeed={audioFile ? audioFile.name : postId}
                      >
                        <source src={audioUrl ?? post.mp3_file} type="audio/mpeg" />
                      </AudioPlayer>
                    </div>
                  )}
                </FileDropzone>
                {errors.mp3_file && <p className="text-sm font-semibold text-red-primary">{errors.mp3_file}</p>}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-baseline gap-2">
                  <span className="text-sm font-semibold text-gray-700">Sheet music (PDF)</span>
                  <FieldTag />
                </div>
                <FileDropzone
                  accept=".pdf"
                  inputLabel="Sheet music (PDF)"
                  isAcceptedFile={isPdfFile}
                  rejectMessage={(file) => `"${file.name}" isn't a PDF. Choose a PDF file.`}
                  onFile={selectPdfFile}
                  hasFile={hasPdf}
                  emptyTitle="Drag a PDF here, or"
                  emptyHint="PDF only."
                >
                  {(openPicker) => (
                    <div className="flex flex-col gap-2">
                      <FileRow
                        icon={<CiFileOn />}
                        name={pdfFile ? pdfFile.name : fileNameFromUrl(post.pdf_file)}
                        meta={pdfFile ? `New file · ${formatSize(pdfFile.size)}` : "Current file"}
                        onReplace={openPicker}
                        onUndo={pdfFile ? undoPdfFile : undefined}
                      />
                      <iframe
                        title="Sheet music preview"
                        src={pdfUrl ?? post.pdf_file}
                        className="w-full h-[420px] rounded-2xl border border-gray-e8 bg-gray-f6"
                      />
                    </div>
                  )}
                </FileDropzone>
              </div>
            </section>
          </div>

          {postId && post.sections.length > 0 && (
            <section className="flex flex-col gap-3 mt-4">
              <div className="flex justify-between items-baseline gap-3 flex-wrap">
                <h2 className="text-2xl font-semibold">Collaboration section</h2>
                <p className="text-[13px] text-gray-70">
                  Listen here. To approve, reject or mix audios, open the{" "}
                  <Link to={`/post-details/${postId}`} className="text-primary font-semibold underline underline-offset-[3px]">project page</Link>.
                </p>
              </div>
              {post.sections.map((section) => {
                const count = section.audio_files.length;
                return (
                  <div key={section.id} className="bg-white rounded-[20px] border border-gray-e8 overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 768:px-5 py-3 bg-secondary">
                      <h3 className="font-semibold">{section.instrument.name}</h3>
                      <span className="text-xs font-bold text-primary bg-white/70 rounded-full px-2.5 py-0.5">
                        {count} {count === 1 ? "audio" : "audios"}
                      </span>
                    </div>
                    {count > 0 ? (
                      section.audio_files.map((audio) => {
                        const status = STATUS_LABEL[audio.status];
                        return (
                          <div
                            key={audio.id}
                            className="grid 768:grid-cols-[minmax(150px,200px)_minmax(0,1fr)_auto] gap-x-4 gap-y-2.5 items-center px-4 768:px-5 py-3 border-t border-gray-e8 first:border-t-0"
                          >
                            <Link to={`/profile/${audio.user}`} className="flex items-center gap-2.5 min-w-0 group">
                              <img
                                src={audio.user_info.profile_photo || "/Images/UserProfile.png"}
                                alt=""
                                className="w-8 h-8 border rounded-full object-cover shrink-0"
                              />
                              <span className="flex flex-col min-w-0">
                                <span className="text-sm font-semibold truncate group-hover:underline">{audio.user_info.username}</span>
                                {audio.created_at && <span className="text-xs text-gray-70">Added {formatDMYHM(audio.created_at)}</span>}
                              </span>
                            </Link>
                            <AudioPlayer src={audio.audio_file} waveformSeed={String(audio.id)} className="w-full min-w-0" />
                            {status && (
                              <span className={`justify-self-start 768:justify-self-auto text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${status.className}`}>
                                {status.text}
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="px-4 768:px-5 py-4 text-gray-70">No audios added yet for {section.instrument.name}.</p>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* Stays in view while scrolling. Cancel is type="button" and Save is
              the form's only submit button: before, both were implicit submit
              buttons, so Save sent the request twice (onClick + form submit)
              and Cancel saved the form on its way out. */}
          <div className="sticky bottom-4 z-10 mt-2 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-e8 bg-white/90 backdrop-blur px-5 py-3 shadow-lg">
            <span className="text-sm text-gray-70">
              {postId ? "Changes are visible to everyone once you save." : "Your project is public once you publish it."}
            </span>
            <div className="flex items-center gap-2.5 ml-auto">
              <Button type={ButtonTypes.CANCEL} htmlType="button" onClick={() => navigate(-1)} className="!px-4 !py-2 font-semibold" disabled={submitting}>
                Cancel
              </Button>
              <Button type={ButtonTypes.PRIMARY} htmlType="submit" onClick={() => {}} className="!px-5 !py-2 font-semibold" disabled={submitting}>
                {submitting ? "Saving..." : postId ? "Save changes" : "Publish project"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddEditPost;
